import appConfig from '../../config/appConfig';

import ClientsideLocations from '../../api/ClientsideLocations';
import ClientsideFacilities from '../../api/ClientsideFacilities';

const mapTemplate = {
  namespaced: true,
  state: {
    mapView: null,
    facilityImageLinkField: '',
    facilityLinkToFactsheetField: '',
    facilityFactoids: {},
    facilityImageLinkValue: '',
    facilityDisclaimer: '',
    facilityLinkToFactsheetValue: '',
    geographyLinkToFactsheetField: '',
    geographyLinkToFactsheetValue: '',
    usaTitle: '',
    usaSubtitle: '',
    canadaTitle: '',
    canadaSubtitle: '',
    stateTitleTemplate: '',
    stateTitle: '',
    stateSubtitle: '',
    stateSubtitleTemplate: '',
    geographyFactoids: {},
    countryExtents: undefined,
    countryData: undefined,
    selectedItemType: 'None',
    selectedItemValue: '',
    loading: false,
    mapLoaded: false,
    facilityFeatureLayer: undefined,
    pipelineFeatureLayer: undefined,
    transmissionFeatureLayer: undefined,
    facilityFeatureLayerView: undefined,
    locationFeatureLayer: undefined,
    listViewData: undefined,
    isMobile: false
  },
  mutations: {
    setMapView(state, mapView) {
      state.mapView = mapView;
    },
    setFacilityImageLinkField(state, name) {
      state.facilityImageLinkField = name;
    },
    setFacilityLinkToFactsheetField(state, name) {
      state.facilityLinkToFactsheetField = name;
    },
    setFacilityFactoids(state, facilityFactoids) {
      state.facilityFactoids = facilityFactoids;
    },
    setFacilityDisclaimer(state, value) {
      state.facilityDisclaimer = value;
    },
    setFacilityImageLinkValue(state, value) {
      state.facilityImageLinkValue = value;
    },
    setFacilityLinkToFactsheetValue(state, value) {
      state.facilityLinkToFactsheetValue = value;
    },
    setGeographyLinkToFactsheetValue(state, value) {
      state.geographyLinkToFactsheetValue = value;
    },
    initializeMapTemplateState(state, template) {
      state.geographyLinkToFactsheetField =
        template.geographyLinkToFactsheetField;
      state.globalTitle = template.geographyPanelTitleGlobal.title;
      state.globalSubtitle = template.geographyPanelTitleGlobal.subtitle;
      state.usaTitle = template.geographyPanelTitleUSA.title;
      state.usaSubtitle = template.geographyPanelTitleUSA.subtitle;
      state.canadaTitle = template.geographyPanelTitleCanada.title;
      state.canadaSubtitle = template.geographyPanelTitleCanada.subtitle;
      state.stateTitleTemplate = template.geographyPanelTitleState.title;
      state.stateSubtitleTemplate = template.geographyPanelTitleState.subtitle;
      const geoFactoids = template.factoidsGeography;
      // Add 'value' property to factoids so present in state from beginning. This will be populated
      // upon completion of feature query to retrieve actual values for selected facility.
      geoFactoids.forEach((f) => {
        f.value = '';
      });
      state.geographyFactoids = geoFactoids;

      state.facilityImageLinkField = template.facilityImageLinkField;
      state.facilityLinkToFactsheetField =
        template.facilityLinkToFactsheetField;
      const facilFactoids = template.factoidsFacility;
      // Add 'value' property to factoids so present in state from beginning. This will be populated
      // upon completion of feature query to retrieve actual values for selected facility.
      facilFactoids.forEach((f) => {
        f.value = '';
      });
      state.facilityFactoids = facilFactoids;
    },
    setStateTitleAndSubtitle(state, name) {
      state.stateTitle = name[name.length - 1] !== 's'
        ? state.stateTitleTemplate.replace('{stateName}', name)
        : state.stateTitleTemplate.replace('{stateName}’s', name + "’");
    },
    setStateFactoids(state, geographyFactoids) {
      state.geographyFactoids = geographyFactoids;
    },
    setCountryExtents(state, extents) {
      state.countryExtents = extents;
    },
    setCountryData(state, countryData) {
      state.countryData = countryData;
    },
    setSelectedItemType(state, type) {
      state.selectedItemType = type;
    },
    setSelectedItemValue(state, value) {
      state.selectedItemValue = value;
    },
    setLoading(state, value) {
      state.loading = value;
    },
    setMapLoaded(state, value) {
      state.mapLoaded = value;
    },
    setFacilityFeatureLayer(state, value) {
      state.facilityFeatureLayer = value;
    },
    setLocationFeatureLayer(state, value) {
      state.locationFeatureLayer = value;
    },
    setTransmissionFeatureLayer(state, value) {
      state.transmissionFeatureLayer = value
    },
    setPipelineFeatureLayer(state, value) {
      state.pipelineFeatureLayer = value;
    },
    setListViewData(state, value) {
      state.listViewData = value;
    },
    setIsMobile(state, value) {
      state.isMobile = value;
    }
  },
  getters: {
    mapView: (state) => {
      return state.mapView;
    },
    facilityImageLinkField: (state) => {
      return state.facilityImageLinkField;
    },
    facilityLinkToFactsheetField: (state) => {
      return state.facilityLinkToFactsheetField;
    },
    facilityFactoids: (state) => {
      return state.facilityFactoids;
    },
    facilityDisclaimer: (state) => {
      return state.facilityDisclaimer;
    },
    facilityImageLinkValue: (state) => {
      return state.facilityImageLinkValue;
    },
    facilityLinkToFactsheetValue: (state) => {
      return state.facilityLinkToFactsheetValue;
    },
    geographyLinkToFactsheetField: (state) => {
      return state.geographyLinkToFactsheetField;
    },
    geographyLinkToFactsheetValue: (state) => {
      return state.geographyLinkToFactsheetValue;
    },
    geographyTitle: (state) => {
      let value = '';
      if (state.selectedItemType == 'Geography') {
        value = state.stateTitle;
      } else if (state.selectedItemType == 'Country') {
        switch (state.selectedItemValue) {
          case appConfig.countriesApiProps.usaCountryValue:
            value = state.usaTitle;
            break;
          case appConfig.countriesApiProps.canadaCountryValue:
            value = state.canadaTitle;
            break;
          case appConfig.countriesApiProps.globalCountryValue:
            value = state.globalTitle;
            break;
        }
      } else {
        value = '';
      }
      return value;
    },
    geographySubtitle: (state) => {
      let value = '';
      if (state.selectedItemType == 'Geography') {
        value = state.stateSubtitle;
      } else if (state.selectedItemType == 'Country') {
        switch (state.selectedItemValue) {
          case appConfig.countriesApiProps.usaCountryValue:
            value = state.usaSubtitle;
            break;
          case appConfig.countriesApiProps.canadaCountryValue:
            value = state.canadaSubtitle;
            break;
          case appConfig.countriesApiProps.globalCountryValue:
            value = state.globalSubtitle;
            break;
        }
      } else {
        value = '';
      }
      return value;
    },
    geographyFactoids: (state) => {
      return state.geographyFactoids;
    },
    countryExtents: (state) => {
      return state.countryExtents;
    },
    countryData: (state) => {
      return state.countryData;
    },
    selectedItemType: (state) => {
      return state.selectedItemType;
    },
    selectedItemValue: (state) => {
      return state.selectedItemValue;
    },
    loading: (state) => {
      return state.loading;
    },
    mapLoaded: (state) => {
      return state.mapLoaded;
    },
    drawer(state, getters, rootState, rootGetters) {
      return rootGetters.getDrawer;
    },
    facilityFeatureLayer: (state) => {
      return state.facilityFeatureLayer;
    },
    locationFeatureLayer: (state) => {
      return state.locationFeatureLayer;
    },
    pipelineFeatureLayer: (state) => {
      return state.pipelineFeatureLayer;
    },
    transmissionFeatureLayer: (state) => {
      return state.transmissionFeatureLayer;
    },
    listViewData: (state) => {
      return state.listViewData;
    },
    isMobile: (state) => {
      return state.isMobile;
    }
  },
  actions: {
    async selectItem({ commit, state, getters, rootGetters }, { value, type }) {
      commit('setLoading', true);
      const get = async () => {
        let data;
        if (type == 'Country') {
          // TODO - Refactor this country code into an API or helper class method
          // TODO 2 - Add error checking here in case extent not set?
          const extent = state.countryExtents.filter((e) => e.name == value)[0]
            .extent;
          const countryData = state.countryData.filter(
            (c) =>
              c[appConfig.countriesApiProps.countryField] &&
              c[appConfig.countriesApiProps.countryField].toLowerCase() ==
              value.toLowerCase()
          )[0];
          const factoids = state.geographyFactoids;
          const updatedFactoids = [];
          if (factoids && countryData) {
            factoids.forEach((f) => {
              updatedFactoids.push({
                field: f.field,
                label: f.label,
                text: f.text,
                format: f.format,
                value: countryData[f.field]
              });
            });
          }
          data = {
            name: value,
            updatedFactoids: updatedFactoids,
            extent: extent
          };
        } else if (type === 'Geography') {
          const locations = new ClientsideLocations(appConfig.locationApiProps);
          data = await locations
            .getLocationInfoByName(
              state.locationFeatureLayer,
              value,
              state.geographyLinkToFactsheetField,
              state.geographyFactoids
            )
            .catch((err) => {
              console.log(`Error getting location '${value}':`);
              console.log(err);
              commit('setError', err, {
                root: true
              });
            });
        } else if (type === 'Facility') {
          const facilities = new ClientsideFacilities(
            appConfig.facilityApiProps
          );
          const facilityDefaultProfile =
            rootGetters['profileConfig/facilityDefaultProfile'];
          const profile = facilityDefaultProfile.templates[0];
          const facilityFactoids = profile.facilityFactoids;
          const facilityFields = profile.outFields;
          const techType = profile.facilityTechType;
          data = await facilities
            .getFacilityInfoByName(
              state.facilityFeatureLayer,
              value,
              state.facilityImageLinkField,
              state.facilityLinkToFactsheetField,
              techType,
              facilityFactoids,
              facilityFields,
              true
            )
            .catch((err) => {
              console.log(`Error getting facility '${value}':`);
              console.log(err);
              commit('setError', err, {
                root: true
              });
            });
        }

        if (data) {
          commit('setSelectedItemType', type);
          commit('setSelectedItemValue', data.name);
          if (state.mapView) {
            console.log(`Zooming to location '${data.name}'...`);
            // console.log(data.extent);
            state.mapView.goTo(data.extent);
            if (getters.drawer != true) {
              console.log(
                'MAPTEMPLATE: rootState.drawer: ' +
                getters.drawer +
                ' MapLoaded: ' +
                getters.mapLoaded
              );
              commit('toggleDrawer', null, {
                root: true
              });
            }
          }

          switch (type) {
            case 'Country':
              commit('setStateFactoids', data.updatedFactoids);
              commit('setGeographyLinkToFactsheetValue', "");
              commit('setFacilityLinkToFactsheetValue', "");
              break;
            case 'Geography':
              commit('setStateTitleAndSubtitle', data.name);
              commit('setGeographyLinkToFactsheetValue', data.factsheetLink);
              commit('setFacilityLinkToFactsheetValue', data.factsheetLink);
              commit('setStateFactoids', data.updatedFactoids);
              break;
            case 'Facility':
              commit('setFacilityImageLinkValue', data.imageLink);
              commit('setFacilityLinkToFactsheetValue', data.factsheetLink);
              commit('setGeographyLinkToFactsheetValue', data.factsheetLink);
              commit('setFacilityFactoids', data.updatedFactoids);
              commit('setFacilityDisclaimer', data.disclaimer);
              break;
          }
          commit('setLoading', false);
        } else {
          console.log(`No location found for '${value}'`);
          commit('setSelectedItemType', 'None');
          commit('setSelectedItemValue', '');
          commit('setLoading', false);
        }
      };
      get();
    },
    async setFacilityStateValues({ commit }, { featureLayer }) {
      // console.log(`mapTemplate action - setFacilityStateValues`);
      commit('setFacilityFeatureLayer', featureLayer);

      // Set facilities on filterData module (for Search box source)
      const facilities = new ClientsideFacilities(appConfig.facilityApiProps);
      await facilities
        .getFacilities(featureLayer)
        .then((result) => {
          commit('filterData/setFacilities', result, {
            root: true
          });
          commit('filterData/setFacilitiesLoaded', true, {
            root: true
          });
        })
        .catch((err) => {
          console.log(`Error setting facilities:`);
          console.log(err);
          commit(
            'setError',
            'Facilities: Error ' + err.details.httpStatus + ' - ' + err.message,
            {
              root: true
            }
          );
        });
      // Set status types on filterData module (for Status dropdown content)
      await facilities
        .getStatusValues(featureLayer)
        .then((result) => {
          commit('filterData/setStatusValues', result, {
            root: true
          });
          commit('filterData/setStatusValuesLoaded', true, {
            root: true
          });
        })
        .catch((err) => {
          console.log(`Error setting status values:`);
          console.log(err);
          commit(
            'setError',
            'Status Values: Error ' +
            err.detail.httpStatus +
            ' - ' +
            err.message,
            {
              root: true
            }
          );
        });
    },
    async setLocationStateValues({ commit }, { featureLayer }) {
      console.log(`mapTemplate action - setLocationStateValues`);
      commit('setLocationFeatureLayer', featureLayer);

      // Set locations on filterData module (for Locations dropdown content)
      const locations = new ClientsideLocations(appConfig.locationApiProps);
      await locations
        .getLocations(featureLayer)
        .then((result) => {
          commit('filterData/setLocations', result, {
            root: true
          });
          commit('filterData/setLocationsLoaded', true, {
            root: true
          });
        })
        .catch((err) => {
          console.log(`Error setting locations:`);
          console.log(err);
          commit(
            'setError',
            'Locations: Error ' + err.details.httpStatus + ' - ' + err.message,
            {
              root: true
            }
          );
        });
    },
    async setMapFilter({ commit, state, getters, rootGetters }) {
      const setFilter = async () => {
        if (state.mapView) {
          const energyFilter = rootGetters['filterData/mapFilterEnergyClause'];
          const facilityLayerView = state.mapView.allLayerViews.find(
            (lv) => lv.layer.title === 'NextEraFacilities'
          );
          const pipelineLayerView = state.mapView.allLayerViews.find(
            (lv) => lv.layer.title === 'NextEraPipelines'
          );
          if (pipelineLayerView) {
            const toggle = (energyFilter === "1=1" || energyFilter.toLowerCase().indexOf("pipeline") > -1);
            pipelineLayerView.layer.visible = toggle;
            pipelineLayerView.filter = {
              where:
                rootGetters['filterData/mapFilterStatusClause']
            }
          }
          const transmissionLayerView = state.mapView.allLayerViews.find(
            (lv) => lv.layer.title === 'NextEraTransmission'
          );
          if (transmissionLayerView) {
            const toggle = energyFilter === "1=1" || energyFilter.toLowerCase().indexOf("transmission line") > -1;
            transmissionLayerView.layer.visible = toggle;
            transmissionLayerView.filter = {
              where:
                rootGetters['filterData/mapFilterStatusClause']
            }
          }
          if (facilityLayerView) {
            facilityLayerView.filter = {
              where:
                rootGetters['filterData/mapFilterStatusClause'] +
                ' AND ' +
                energyFilter
            };
          }
        }
      };
      setFilter();
    },
    async setListViewData({ commit, state }) {
      const get = async (state) => {
        const facilities = new ClientsideFacilities(appConfig.facilityApiProps);
        // TODO - NOTE these column names should be coming from the listView configuration
        // section of the *Default Map Template*. I am just hard-coding them here for
        // the sake of expediency in this example code. You also would get the column
        // headings (labels) from the same config section in the Default Map Template.
        const dataColumns = new Array(
          'FUEL_TYPE_DESC',
          'FACILITY_IMAGE',
          'PROJECT_STATUS',
          'PROJECT_NAME',
          'STATE_NAME',
          'COUNTY_NAME',
          'US_CONG_DISTRICT',
          'MW'
        );
        await facilities
          .getListViewData(state.facilityFeatureLayer, dataColumns)
          .then((result) => {
            commit('setListViewData', result);
          })
          .catch((err) => {
            console.log(`Error Setting ListViewData:`);
            console.log(err);
            commit(
              'setError',
              'List View Data: Error ' +
              err.details.httpStatus +
              ' - ' +
              err.message,
              {
                root: true
              }
            );
          });
      };
      get(state);
    }
  }
};

export default mapTemplate;
